import type { LocationPlaceValue } from '@repo/features/location'
import type { TimePickerValue } from '@repo/features/time-picker'

import { calendarDayNumber, copyLocalCalendarDate } from './calendar-date.ts'

export type QuickCreateStop = {
  date: Date
  id: string
  place: LocationPlaceValue | null
  reference_no?: null | string
  time: TimePickerValue
  type: QuickCreateStopType
}

export type QuickCreateStopType = 'collection' | 'delivery'

export type RouteMoveDirection = 'down' | 'up'

export const addCalendarDays = (date: Date, days: number) => {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  result.setDate(result.getDate() + days)
  return result
}

export const cloneStop = (stop: QuickCreateStop): QuickCreateStop => ({
  ...stop,
  date: copyLocalCalendarDate(stop.date),
  place: stop.place ? { ...stop.place } : null,
  reference_no: stop.reference_no ?? null,
  time: { ...stop.time },
})

const newStop = (id: string, type: QuickCreateStopType, date: Date): QuickCreateStop => ({
  date,
  id,
  place: null,
  reference_no: null,
  time: type === 'collection' ? { from: '08:00', to: '16:00', type: 'between' } : { type: 'asap' },
  type,
})

export const createFreshRoute = (today = new Date()): QuickCreateStop[] => {
  const collectionDate = addCalendarDays(today, 1)
  return [
    newStop('quick-create-collection-0', 'collection', collectionDate),
    newStop('quick-create-delivery-1', 'delivery', addCalendarDays(collectionDate, 1)),
  ]
}

export const isValidDate = (date: Date) => date instanceof Date && !Number.isNaN(date.getTime())

/** The date of the nearest dated Stop before `index`; a Stop cannot be earlier than it. */
export const getEarliestAllowedDate = (stops: readonly QuickCreateStop[], index: number) =>
  stops
    .slice(0, index)
    .filter(stop => isValidDate(stop.date))
    .at(-1)?.date

export const isValidRouteStructure = (stops: readonly QuickCreateStop[]) =>
  stops.length >= 2 && stops[0]?.type === 'collection' && stops.at(-1)?.type === 'delivery'

export const toggleStopType = (stops: readonly QuickCreateStop[], index: number) => {
  if (!isValidRouteStructure(stops) || index <= 0 || index >= stops.length - 1) return stops
  return stops.map((stop, i) =>
    i === index
      ? { ...cloneStop(stop), type: stop.type === 'collection' ? 'delivery' : 'collection' }
      : stop,
  )
}

export const insertAfterActiveStop = (stops: readonly QuickCreateStop[], activeIndex: number) => {
  if (!isValidRouteStructure(stops) || activeIndex < 0 || activeIndex >= stops.length) return stops
  const index = activeIndex === stops.length - 1 ? activeIndex : activeIndex + 1
  const previous = stops[index - 1]
  const following = stops[index]
  let date = addCalendarDays(previous.date, 1)
  if (following && calendarDayNumber(date) > calendarDayNumber(following.date))
    date = new Date(following.date)
  const usedIds = new Set(stops.map(stop => stop.id))
  let suffix = stops.length
  while (usedIds.has(`quick-create-${suffix}`)) suffix += 1
  const inserted = newStop(`quick-create-${suffix}`, 'collection', date)
  return [...stops.slice(0, index), inserted, ...stops.slice(index)]
}

export const canDeleteStop = (stops: readonly QuickCreateStop[], index: number) =>
  isValidRouteStructure(stops) && stops.length > 2 && index > 0 && index < stops.length - 1

export const deleteStop = (stops: readonly QuickCreateStop[], index: number) =>
  canDeleteStop(stops, index) ? stops.filter((_, i) => i !== index) : stops

const moved = (stops: readonly QuickCreateStop[], index: number, direction: RouteMoveDirection) => {
  if (!isValidRouteStructure(stops)) return null
  const target = direction === 'up' ? index - 1 : index + 1
  if (index <= 0 || index >= stops.length - 1 || target <= 0 || target >= stops.length - 1)
    return null
  const result = [...stops]
  ;[result[index], result[target]] = [result[target], result[index]]
  return result
}

export const canMoveStop = (
  stops: readonly QuickCreateStop[],
  index: number,
  direction: RouteMoveDirection,
) => {
  const result = moved(stops, index, direction)
  return (
    result !== null &&
    result.every(
      (stop, i) => i === 0 || calendarDayNumber(result[i - 1].date) <= calendarDayNumber(stop.date),
    )
  )
}

export const moveStop = (
  stops: readonly QuickCreateStop[],
  index: number,
  direction: RouteMoveDirection,
) => (canMoveStop(stops, index, direction) ? moved(stops, index, direction)! : stops)
